import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);
  // Active view-as role for users holding multiple roles (UI-only)
  const [activeRole, setActiveRole] = useState(null);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setActiveRole((prev) => prev && data.roles.includes(prev) ? prev : data.primary_role);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  // Global expiry signal from api.js axios interceptor — fired when an
  // automatic /auth/refresh attempt fails. We mark the user as logged-out
  // so ProtectedRoute redirects to /auth/login instead of leaving the
  // dashboard stuck on a raw "Not authenticated" error string.
  useEffect(() => {
    const onExpired = () => { setUser(false); setActiveRole(null); };
    window.addEventListener("lifted:auth-expired", onExpired);
    return () => window.removeEventListener("lifted:auth-expired", onExpired);
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      setActiveRole(data.primary_role);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: apiErrorMessage(e) };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      setActiveRole(data.primary_role);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: apiErrorMessage(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    // Phase 19 — defensively clear EVERY known persistence layer so the
    // mobile footer can never show a previous user's identity:
    //   • cookies (server-side delete already happened, but mirror client)
    //   • localStorage / sessionStorage (clear app-scoped keys only)
    //   • react state (setUser(false))
    setUser(false);
    setActiveRole(null);
    try {
      // Clear any legacy/cached user state — explicit list so we don't
      // nuke unrelated app prefs (e.g. theme, age-gate).
      ["lifted_user", "lifted_user_profile", "lifted_active_role",
       "lifted_navhelp_session_bumped", "lifted_inbox_cache"].forEach((k) => {
        try { localStorage.removeItem(k); } catch (_) { /* noop */ }
        try { sessionStorage.removeItem(k); } catch (_) { /* noop */ }
      });
      // Browser-level cookie wipe as a belt-and-suspenders fallback. The
      // server-side delete_cookie call is the authoritative path; this
      // catches the case where the browser dropped the deletion cookie.
      ["access_token", "refresh_token"].forEach((name) => {
        document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure`;
      });
    } catch (_) { /* noop */ }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: apiErrorMessage(e) };
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, activeRole, setActiveRole, login, register, logout, forgotPassword, refreshMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
